import { Card, Label, TextInput } from "flowbite-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import plans from "../config/plans";
import { useState } from "react";

/* 🔄 Spinner Component */
function Spinner({ text }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-purple-800 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm font-semibold text-gray-700">{text}</p>
      </div>
    </div>
  );
}

export default function Checkout() {
  const [params] = useSearchParams();
  const planCode = params.get("plan");
  const navigate = useNavigate();

  const plan = plans[planCode];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  const isNameValid = name.trim().length > 0;

  const isEmailValid = email.trim().length > 0 && email.includes("@");

  const isPhoneValid = /^\d{10}$/.test(phoneNumber); // exactly 10 digits

  const isFormValid = isNameValid && isEmailValid && isPhoneValid;

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        Invalid plan selected
      </div>
    );
  }

  const handlePay = async () => {
    if (!isFormValid) return;

    setIsCreatingOrder(true);

    try {
      // 1️⃣ Create order (DB saved as PENDING)
      const res = await fetch(
        "https://fitmeal-payment-api-1.onrender.com/api/payment/create-order",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            amount: plan.amount,
            planCode,
            name,
            email,
            phoneNumber,
          }),
        }
      );

      const data = await res.json();
      setIsCreatingOrder(false);

      // 2️⃣ Open Razorpay
      const options = {
        key: data.key,
        amount: data.amount * 100,
        currency: "INR",
        name: "FitMeal Workout",
        description: plan.name,
        order_id: data.orderId,

        handler: function () {
          // 3️⃣ Navigate to success page with orderId
          navigate(`/success?orderId=${data.orderId}`);
        },

        modal: {
          ondismiss: () => setIsCreatingOrder(false),
        },

        theme: {
          color: "#54085e",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setIsCreatingOrder(false);
      alert("Payment initiation failed");
      console.error(err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <h1 className="text-2xl font-bold text-center text-purple-800">
          FitMeal Workout
        </h1>

        <div className="border rounded-lg p-4 bg-green-50">
          <h2 className="text-lg font-semibold">{plan.name}</h2>
          <p className="text-2xl font-bold mt-2">₹{plan.amount}</p>

          <ul className="mt-4 space-y-2 text-sm text-gray-700">
            {plan.features.map((feature, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-green-600 mt-0.5">✔</span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-3">
          <div>
            <Label value="Full Name" />
            <TextInput
              value={name}
              placeholder="Enter FullName"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div>
            <Label value="Email" />
            <TextInput
              type="email"
              placeholder="Enter Valid Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <Label value="Phone Number" />
            <TextInput
              value={phoneNumber}
              placeholder="Enter PhoneNumber"
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
        </div>

        <button
          onClick={handlePay}
          disabled={!isFormValid || isCreatingOrder}
          className={`w-full mt-6 py-3 rounded-lg text-lg font-semibold shadow-md transition
            ${
              !isFormValid || isCreatingOrder
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-purple-800 hover:bg-purple-900 text-white"
            }`}
        >
          {isCreatingOrder
            ? "Redirecting to payment..."
            : `Pay ₹${plan.amount} Securely`}
        </button>

        <p className="text-xs text-center text-gray-500">
          100% Secure Payments · Powered by Razorpay
        </p>
      </Card>

      {isCreatingOrder && <Spinner text="Redirecting to payment gateway..." />}
    </div>
  );
}
